import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Crown, Plus, Loader2, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function TenantTiers() {
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState(null);
  const [form, setForm] = useState({ name: "", rank: 1, min_points: 0, color: "47 96% 53%", benefits: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/tenant/tiers"); setItems(data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form, rank: Number(form.rank), min_points: Number(form.min_points), benefits: form.benefits.split("\n").map(s => s.trim()).filter(Boolean) };
      await api.post("/tenant/tiers", payload);
      toast.success("Nível criado — clientes recalculados");
      setOpen(false);
      setForm({ name: "", rank: 1, min_points: 0, color: "47 96% 53%", benefits: "" });
      load();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este nível?")) return;
    await api.delete(`/tenant/tiers/${id}`); load();
  };

  const openMembers = async (tier) => {
    try {
      const { data } = await api.get(`/tenant/tiers/clients/${tier.id}`);
      setMembers(data);
    } catch (er) { toast.error(formatApiError(er)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
            <Crown className="w-3.5 h-3.5" /> Clube de Benefícios
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Níveis de Clientes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Promova clientes automaticamente conforme acumulam pontos. Cada subida de nível dispara automações.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="tier-add" className="gap-2"
              style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
              <Plus className="w-4 h-4" /> Novo nível
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Novo nível</DialogTitle>
              <DialogDescription>Ex.: Bronze (ordem 1, mínimo 0 pts) · Prata (ordem 2, 500 pts) · Ouro (ordem 3, 2000 pts)</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Nome</Label><Input required data-testid="tier-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Ordem</Label><Input required type="number" min={1} max={20} value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Pontos mínimos</Label><Input required type="number" min={0} value={form.min_points} onChange={(e) => setForm({ ...form, min_points: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Cor do nível (formato HSL)</Label>
                <div className="flex gap-2">
                  <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="47 96% 53%" />
                  <div className="w-10 h-10 rounded-md border border-border" style={{ background: `hsl(${form.color || "0 0% 50%"})` }} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Benefícios (um por linha)</Label>
                <Textarea rows={3} value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} placeholder="10% de desconto em qualquer serviço&#10;Brinde no aniversário" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} data-testid="tier-submit" className="gap-2"
                  style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Criar nível
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="p-10 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div> :
        items.length === 0 ? (
          <Card className="p-12 text-center text-muted-foreground rounded-tenant">
            Nenhum nível criado ainda. Crie pelo menos um para começar a promover clientes.
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((t) => (
              <Card key={t.id} className="p-5 rounded-tenant border-border overflow-hidden">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: `hsl(${t.color})` }}>
                      <Crown className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-lg">{t.name}</div>
                      <div className="text-xs text-muted-foreground">Rank {t.rank} · {t.min_points.toLocaleString("pt-BR")}+ pontos</div>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(t.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
                {(t.benefits || []).length > 0 && (
                  <ul className="text-xs text-muted-foreground space-y-1 mb-3">
                    {t.benefits.map((b, i) => <li key={i}>• {b}</li>)}
                  </ul>
                )}
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" /> {t.members_count || 0} membros
                  </span>
                  <Button size="sm" variant="outline" onClick={() => openMembers(t)}>Ver membros</Button>
                </div>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={!!members} onOpenChange={(o) => !o && setMembers(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Membros de {members?.tier?.name}</DialogTitle>
            <DialogDescription>{members?.items?.length || 0} clientes neste nível</DialogDescription>
          </DialogHeader>
          {(members?.items || []).length === 0 ? (
            <div className="text-sm text-muted-foreground">Sem clientes ainda neste nível.</div>
          ) : (
            <ul className="divide-y divide-border max-h-96 overflow-y-auto">
              {members.items.map((c) => (
                <li key={c.id} className="py-2 flex justify-between text-sm">
                  <span>{c.name}</span>
                  <span className="font-mono font-semibold text-tenant">{c.points_balance} pts</span>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
